<template>
  <div
    :id="`${id}`"
    v-click-outside="hideDropdown"
    class="rs-dropdown-checkboxes checkbox-select"
  >
    <label v-if="label">
      {{ label }}

      <div
        v-if="showInfo"
        class="info-icon cursor-pointer"
        style="padding: 2px 2.5px 2.7px 2.5px; margin-left: 6px;"
        @click="$emit('on-info-click')"
      >
        <RsIcon
          icon="info"
          weight="fa"
        />
      </div>
    </label>

    <div
      class="checkbox-select__trigger row m-0"
      :class="{ disabled, isActive: activeTrigger }"
      @click="showDropdown"
    >
      <div
        v-if="!inputDisplay"
        class="checkbox-select__title dropdown-display"
      >
        {{ placeholder }}
      </div>

      <div
        v-else
        class="checkbox-select__title truncate dropdown-display pr-2"
        :title="inputDisplay"
      >
        {{ inputDisplay }}
      </div>
      <i
        v-show="activeTrigger"
        class="far fa-chevron-up"
      />
      <i
        v-show="!activeTrigger"
        class="far fa-chevron-down"
      />
    </div>
    <!-- Dropdown -->
    <div
      v-if="activeTrigger"
      class="checkbox-select__dropdown"
      @focusout="handleDropdownFocusout"
    >
      <!-- Search -->
      <div class="checkbox-select__search-wrap">
        <RsFormInput
          ref="search"
          v-model="search"
          :debounce="300"
          type="search"
          icon="search"
        />
      </div>
      <!-- Select All row -->

      <div
        v-show="hasSelectAll"
        class="checkbox-select__col"
      >
        <div class="checkbox-select__select-all">
          <label
            @click="setAllSelected()"
          >{{ selectAllLabel }}</label>
        </div>
      </div>

      <!-- checkboxes and labels -->
      <OverlayScrollbars class="checkbox-select__filters-wrap">
        <ul>
          <li
            v-for="(item, index) in filteredList"
            :key="`${group}_${index}`"
          >
            <label
              v-rs-tooltip.hover.right="item.tooltip || ''"
              class="checkbox-select__check-wrap center container"
            >
              <input
                :id="
                  `${index}_${item.text.replace(/ /g, '')}_${group?.replace(
                    / /g,
                    '',
                  )}`
                "
                v-model="input"
                type="checkbox"
                :name="
                  `${index}_${item.text.replace(/ /g, '')}_${group?.replace(
                    / /g,
                    '',
                  )}`
                "
                :value="item.value"
                :disabled="isOptionDisabled(item)"
                @change="handleOptionChange($event, item.value)"
              >
              <span class="checkmark" :class="{ 'rs-bg-content': isOptionDisabled(item) }" />
              <div class="label">
                {{ item.text }}
                <span
                  v-if="item.subText"
                  class="sub-text"
                  :class="{ 'conflict-text': /conflict/i.test(item.subText) }"
                >
                  {{ item.subText }}
                </span>
              </div>
            </label>
          </li>
        </ul>
      </OverlayScrollbars>
      <footer class="checkbox-select__footer">
        <span class="footer-text mr-3">{{ footerText }}</span>
        <RsButton
          variant="primary"
          @click="handleApply"
        >
          {{ buttonText || $ts('actions.apply') }}
        </RsButton>
      </footer>
    </div>
  </div>
</template>

<script lang="ts">
import { ComponentPublicInstance, defineComponent, PropType } from 'vue';
import { ISelectOption } from '@/shared';
import RsIcon from '@/components/_global/RsIcon.vue';

export default defineComponent({
  name: 'RsDropdownCheckboxes',
  components: { RsIcon },
  props: {
    id: {
      type: String,
      default: '',
    },
    showInfo: {
      type: Boolean,
      default: false,
    },
    label: String,
    group: String,
    disabled: {
      type: Boolean,
      default: false,
    },
    hasSelectAll: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: 'Select',
    },
    options: {
      type: Array as PropType<ISelectOption[]>,
      default: () => [],
    },
    value: {
      type: Array,
      default: () => [],
    },
    /**
     * Vue 3 v-model binding, kept alongside the legacy `value`/`input` pair so
     * both `v-model` and explicit `:value`+`@input` consumers work. `undefined`
     * means the parent did not bind v-model (see the `boundValue` computed).
     */
    modelValue: {
      type: Array,
      default: undefined,
    },
    displayProperty: {
      type: String,
      default: '',
    },
    buttonText: {
      type: String,
      default: '',
    },
    displayList: {
      type: Boolean,
      default: true,
    },
    listingScoresHeart: {
      type: Boolean,
      default: false,
    },
    noItemSelectedDefault: {
      type: Boolean,
      default: false,
    },
    replaceAllWithPlaceholder: {
      type: Boolean,
      default: false,
    },
    footerText: {
      type: String,
      default: '',
    },
    maxSelect: {
      type: Number,
      default: null,
    },
    /**
     * When enabled with `maxSelect=1`, selecting a new option replaces
     * the previous selection (radio-like behavior) instead of requiring
     * manual deselection first.
     */
    replaceOnMaxSelect: {
      type: Boolean,
      default: false,
    },
    /**
     * Optional function used to normalize option text and search input
     * before comparison (e.g. strip formatting, digits-only, accent removal).
     */
    normalizeSearch: {
      type: Function as PropType<(value: string) => string>,
      default: null,
    },
  },

  emits: ['input', 'on-info-click', 'update:modelValue'],
  data() {
    const initialValue = this.modelValue !== undefined ? this.modelValue : this.value;
    return {
      search: '',
      input: initialValue ? [...initialValue] : [],
      selectAllText: 'Select All',
      activeTrigger: false,
      dropdown: false,
      inputDisplay: '',
      isMounted: false,
    };
  },
  computed: {
    /**
     * The externally-bound value, preferring the Vue 3 `modelValue` (v-model)
     * when present and falling back to the legacy `value` prop otherwise.
     */
    boundValue(): unknown[] {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },
    filteredList(): ISelectOption[] {
      const search = this.search.toLowerCase();
      const normalizedSearch = this.normalizeSearch ? this.normalizeSearch(search) : search;

      return (this.options ?? []).filter(({ text = '' }) => {
        const value = text.toLowerCase();

        if (!this.normalizeSearch) {
          return value.includes(normalizedSearch);
        }

        return this.normalizeSearch(value).includes(normalizedSearch);
      });
    },
    allSelected: {
      get(): boolean {
        return !this.input.length;
      },
      set(newValue: boolean): unknown {
        return newValue;
      },
    },

    selectAllLabel(): string {
      const action = this.allSelected && this.input.length !== 0
        ? this.$ts('actions.clearAll')
        : this.$ts('actions.selectAll');
      const count = this.$tc('actions.selected', this.input.length, { count: this.input.length });
      return `${action} (${count})`;
    },
  },
  watch: {
    boundValue: {
      deep: true,
      immediate: true,
      handler(newValue: string[] | number[]) {
        this.input = newValue;
        this.setInputDisplay();
        // Vue 3 crashes on $forceUpdate() before mount (immediate watcher runs
        // pre-mount); the initial render already reflects current state.
        if (this.isMounted) this.$forceUpdate();
      },
    },

    input(): void {
      this.allSelected = ((this.input || []).length >= (this.filteredList || []).length) || (this.input || []).length === 0;
    },

    activeTrigger(): void {
      if (!this.activeTrigger) {
        this.triggerSafetyFocusout();
      }
    },
  },
  mounted() {
    this.isMounted = true;
    this.setInputDisplay();
  },

  methods: {
    handleOptionChange(event: Event, value: string | number): void {
      if (!this.replaceOnMaxSelect || this.maxSelect !== 1) {
        return;
      }

      const target = event.target as HTMLInputElement;

      this.input = target.checked ? [value] : [];
    },

    setAllSelected(): void {
      if (this.allSelected && this.input.length === 0) {
        this.input = Array.from(
          this.filteredList,
          (v: ISelectOption) => v.value,
        );
      } else {
        this.input = [];
      }
    },

    setInputDisplay(): void {
      if (this.allSelected && !this.displayList && !this.replaceAllWithPlaceholder) {
        this.inputDisplay = `[${this.$t('filters.all')}]`;
      } else if (this.allSelected && !this.displayList && this.replaceAllWithPlaceholder) {
        this.inputDisplay = this.placeholder;
      } else if (this.displayList) {
        this.inputDisplay = Array.from(this.input as (string | number | Record<string, unknown>)[], (i: string | number | Record<string, unknown>) => {
          const opt = this.options.find((option: ISelectOption) => {
            if (
              typeof i === 'object'
                && i?.hasOwnProperty('id')
                && typeof option.value === 'object'
                && option.value.hasOwnProperty('id')
            ) {
              return option.value.id === i.id;
            }

            return option.value === i;
          });

          return opt?.text || '';
        }).join(', ');
      } else if (this.input.length === 0 && this.noItemSelectedDefault) {
        this.inputDisplay = this.placeholder;
      } else {
        this.inputDisplay = this.$tc('actions.selected', this.input.length, { count: this.input.length });
      }
    },

    showDropdown(): void {
      if (!this.disabled) {
        if (!this.dropdown) {
          this.input = [...this.boundValue];
        }

        this.dropdown = !this.dropdown;
        this.activeTrigger = this.dropdown;

        if (this.activeTrigger) {
          setTimeout(() => {
            const searchEl = (this.$refs.search as ComponentPublicInstance).$el as HTMLElement;
            (searchEl.querySelector('input[type=search]') as HTMLElement).focus();
          }, 100);
        }
      }
    },
    hideDropdown(): void {
      if (!this.dropdown) {
        this.input = [...this.boundValue || []];
      }

      this.dropdown = false;
      this.activeTrigger = this.dropdown;
    },
    handleApply(): void {
      this.hideDropdown();
      this.setInputDisplay();
      this.$emit('update:modelValue', this.input);
      this.$emit('input', this.input);
    },
    handleDropdownFocusout(event: Event): void {
      event.preventDefault();
      event.stopPropagation();
    },
    triggerSafetyFocusout(): void {
      const element = document.getElementById(this.id);
      if (element) {
        const safetyfocusout = new Event('focusout', { bubbles: true });
        element.dispatchEvent(safetyfocusout);
      }
    },

    isMaxSelected(value: string | number) {
      if (this.replaceOnMaxSelect && this.maxSelect === 1) {
        return false;
      }

      return !!this.maxSelect
        && this.input.length >= this.maxSelect
        && !this.input.some((v) => `${v}` === `${value}`);
    },
    isOptionDisabled(item: ISelectOption) {
      return !!item.disabled || this.isMaxSelected(item.value);
    },
  },
});
</script>
<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.info-icon {
  background: $info;
  border-radius: 10px;
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 10px;
}

.checkbox-select {
  position: relative;
  margin-bottom: 1rem;
  max-width: 440px;
  width: 100%;

  .dropdown-display {
    flex: 1;
  }

  input[type="checkbox"]{
    display: none !important;
  }
  .checkmark {
    top: 0;
    left: 0;
    height: 18px;
    border-radius: 20%;
    width: 18px;
    border: 1px solid $tertiary-gray;
    background-color: #fff;
  }

  .container input:checked ~ .checkmark {
    background-color: $secondary-color;
  }

  .checkmark:after {
    content: "";
    position: absolute;
    display: none;
  }

  .container input:checked ~ .checkmark:after {
    display: block;
  }

  .container .checkmark:after {
    left: 23px;
    margin-top: 2px;
    width: 5px;
    height: 10px;
    border: solid white;
    border-width: 0 2px 2px 0;
    -webkit-transform: rotate(45deg);
    -ms-transform: rotate(45deg);
    transform: rotate(45deg);
  }

  .label{
    margin: 0 0 0 0.5rem;
    width: 90%;
    font-weight: normal;
    color: $tertiary-muted;
    display: inline;
    .sub-text {
      margin-left: 4px;
      font-size: 11px;
      &.conflict-text {
        color: $error;
        font-weight: 600;
      }
    }
  }
  .center{
    align-items: center;
    cursor: pointer;
  }

  &__trigger {
    width: 100%;
    height: 40px;
    border-radius: 5px;
    border: 1px solid $tertiary-gray;
    font-size: 0.9375rem;
    padding: 0.5rem 1.5rem 0.5rem 0.5rem;
    cursor: pointer;
    align-items: center;
    transition: background-color 0.15s ease-in-out,
      border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
    .spin {
      padding: 0;
      transition: transform 0.2s ease;
    }
    &.isActive {
      border-color: #80bdff;
      outline: 0;
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);

      .spin {
        transform: rotate(180deg);
      }
    }
  }
  &__dropdown {
    position: absolute;
    margin-top: 1rem;
    z-index: 110;
    min-width: 215px;
    border: 1px solid $tertiary-gray;
    background-color: #fff;
    visibility: inherit;
    opacity: 1;
    box-shadow: 0px 3px 4px 1px rgba(0, 0, 0, 0.11);
    border-radius: $global-radius;
    overflow: hidden;
    &:after,
    &:before {
      position: absolute;
      content: "";
      top: 0;
      display: block;
      height: 4px;
      z-index: 1;
    }
  }
  &__search-wrap {
    padding: 0.5rem;
    .rs-input {
      margin-bottom: 0;
    }
  }
  &__filters-wrap {
    ul {
      padding: 0.5rem 0;
      li {
        display: block;
        margin: 0;
      }
    }
  }
  &__col {
    display: flex;
    font-size: 12px;
    padding: 0 10px;
    justify-content: space-between;
    text-transform: uppercase;
  }
  &__select-all {
    label {
      cursor: pointer;
      color: $tertiary-muted;
      font-size: rem-calc(12);
      &:hover {
        color: $secondary-color;
      }
    }
    input {
      display: none;
    }
  }
  &__filters-wrap {
    height: 200px;
    background-color: $tertiary-light-gray;
    border-bottom: 1px solid $tertiary-gray;
    border-top: 1px solid $tertiary-gray;
  }
  &__check-wrap {
    position: relative;
    padding-left: 1rem;
    .rs-checkbox {
      margin: 0.5rem 0;
    }
  }
  &__footer {
    display: flex;
    justify-content: flex-end;
    padding: 0.5rem;
  }

  .footer-text {
    font-size: 12px;
  }
}
</style>
